import React, { useState } from 'react';
import { 
  Key, 
  Eye, 
  EyeOff, 
  Copy, 
  Check, 
  Shield, 
  AlertTriangle,
  RefreshCw,
  Save,
  Trash2,
  Plus,
  Edit,
  Lock,
  Unlock,
  ArrowLeft,
  LogOut
} from 'lucide-react';
import { useDeveloper } from '../../contexts/DeveloperContext';

// Componente para exibir credenciais com opção de mostrar/ocultar
const CredentialField = ({ 
  label, 
  value, 
  type = 'password',
  onEdit,
  onDelete,
  canEdit = true,
  canDelete = false
}: {
  label: string;
  value: string;
  type?: 'password' | 'text';
  onEdit?: (newValue: string) => void;
  onDelete?: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  const handleSave = () => {
    if (onEdit) {
      onEdit(editValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-slate-700">{label}</label>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="p-1 text-slate-500 hover:text-blue-600 transition-colors"
              title="Editar"
            >
              <Edit className="w-4 h-4" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={onDelete}
              className="p-1 text-slate-500 hover:text-red-600 transition-colors"
              title="Excluir"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      {isEditing ? (
        <div className="space-y-3">
          <input
            type={isVisible ? 'text' : 'password'}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Digite o novo valor"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
            >
              <Save className="w-4 h-4" />
              Salvar
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center gap-1 px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2">
            <span className="font-mono text-sm text-slate-600 bg-slate-50 px-2 py-1 rounded flex-1">
              {isVisible ? value : '•'.repeat(Math.min(value.length, 20))}
            </span>
            <button
              onClick={() => setIsVisible(!isVisible)}
              className="p-1 text-slate-500 hover:text-slate-700 transition-colors"
              title={isVisible ? 'Ocultar' : 'Mostrar'}
            >
              {isVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <button
            onClick={handleCopy}
            className="p-1 text-slate-500 hover:text-blue-600 transition-colors"
            title="Copiar"
          >
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      )}
    </div>
  );
};

export const CredentialsPage: React.FC = () => {
  const { logout, setDeveloperPassword } = useDeveloper();
  const [developerPassword, setDeveloperPasswordValue] = useState('');
  const [newDeveloperPassword, setNewDeveloperPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Credenciais do sistema (simuladas - em produção viriam de variáveis de ambiente)
  const [credentials, setCredentials] = useState({
    // Telegram
    telegramBotToken: '1234567890:ABCdefGHIjklMNOpqrsTUVwxyz',
    telegramChatId: '123456789',
    
    // Twilio
    twilioAccountSid: 'ACe66b6406d2568831f4d904c462551cf2',
    twilioAuthToken: '797e17abc9bd7e98ffbb3f2ab726cecb',
    twilioPhoneNumber: '+18723493776',
    
    // Firebase
    firebaseApiKey: 'AIzaSyBvOkBwqRzXvOkBwqRzXvOkBwqRzXvOkBw',
    firebaseAuthDomain: 'playkids-eventos.firebaseapp.com',
    firebaseProjectId: 'playkids-eventos',
    
    // Email
    emailHost: 'smtp.gmail.com',
    emailPort: '587',
    emailUser: 'contato@playkidseventos.com.br',
    emailPassword: 'senha_do_email_aqui',
    
    // Zenvia SMS
    zenviaApiKey: 'sua_zenvia_api_key_aqui',
    zenviaSenderName: 'PlayKids',
    
    // WhatsApp Business
    whatsappAccessToken: 'seu_whatsapp_access_token_aqui',
    whatsappPhoneId: 'seu_phone_id_aqui',
    whatsappBusinessAccountId: 'seu_business_account_id_aqui',
    
    // Outros
    adminPhone: '5533998204921',
    adminEmail: 'admin@playkidseventos.com.br',
    websiteUrl: 'https://playkidseventos.com.br'
  });

  const handleChangeDeveloperPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newDeveloperPassword.length < 6) {
      alert('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    if (newDeveloperPassword !== confirmPassword) {
      alert('As senhas não coincidem');
      return;
    }
    
    setIsChangingPassword(true);
    
    // Simular delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setDeveloperPassword(newDeveloperPassword);
    setNewDeveloperPassword('');
    setConfirmPassword('');
    setIsChangingPassword(false);
    
    alert('Senha de desenvolvedor alterada com sucesso!');
  };

  const handleCredentialEdit = (key: string, newValue: string) => {
    setCredentials(prev => ({
      ...prev,
      [key]: newValue
    }));
  };

  const credentialSections = [
    {
      title: 'Telegram Bot',
      icon: '📱',
      credentials: [
        { key: 'telegramBotToken', label: 'Bot Token', canEdit: true },
        { key: 'telegramChatId', label: 'Chat ID', canEdit: true }
      ]
    },
    {
      title: 'Twilio SMS',
      icon: '📞',
      credentials: [
        { key: 'twilioAccountSid', label: 'Account SID', canEdit: true },
        { key: 'twilioAuthToken', label: 'Auth Token', canEdit: true },
        { key: 'twilioPhoneNumber', label: 'Número Twilio', canEdit: true }
      ]
    },
    {
      title: 'Firebase',
      icon: '🔥',
      credentials: [
        { key: 'firebaseApiKey', label: 'API Key', canEdit: true },
        { key: 'firebaseAuthDomain', label: 'Auth Domain', canEdit: true },
        { key: 'firebaseProjectId', label: 'Project ID', canEdit: true }
      ]
    },
    {
      title: 'Email SMTP',
      icon: '📧',
      credentials: [
        { key: 'emailHost', label: 'Host', canEdit: true },
        { key: 'emailPort', label: 'Porta', canEdit: true },
        { key: 'emailUser', label: 'Usuário', canEdit: true },
        { key: 'emailPassword', label: 'Senha', canEdit: true }
      ]
    },
    {
      title: 'Zenvia SMS',
      icon: '🇧🇷',
      credentials: [
        { key: 'zenviaApiKey', label: 'API Key', canEdit: true },
        { key: 'zenviaSenderName', label: 'Nome do Remetente', canEdit: true }
      ]
    },
    {
      title: 'WhatsApp Business',
      icon: '💬',
      credentials: [
        { key: 'whatsappAccessToken', label: 'Access Token', canEdit: true },
        { key: 'whatsappPhoneId', label: 'Phone ID', canEdit: true },
        { key: 'whatsappBusinessAccountId', label: 'Business Account ID', canEdit: true }
      ]
    },
    {
      title: 'Configurações Gerais',
      icon: '⚙️',
      credentials: [
        { key: 'adminPhone', label: 'Telefone Admin', canEdit: true },
        { key: 'adminEmail', label: 'Email Admin', canEdit: true },
        { key: 'websiteUrl', label: 'URL do Site', canEdit: true }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Key className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-slate-900">PlayKids</h1>
                <p className="text-xs text-slate-500">Credenciais do Sistema</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <a
                href="/admin/contratos"
                className="flex items-center gap-2 px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar para Contratos
              </a>
              <button
                onClick={logout}
                className="flex items-center gap-2 px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                title="Sair do modo desenvolvedor"
              >
                <LogOut className="w-4 h-4" />
                Sair
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Aviso de Segurança */}
        <div className="mb-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800 mb-2">⚠️ Aviso de Segurança</h3>
              <p className="text-sm text-amber-700 mb-3">
                Esta página contém informações sensíveis do sistema. Mantenha-a segura e não compartilhe com pessoas não autorizadas.
              </p>
              <ul className="text-xs text-amber-700 space-y-1">
                <li>• As credenciais são armazenadas localmente no navegador</li>
                <li>• Use apenas em computadores confiáveis</li>
                <li>• Faça logout após o uso</li>
                <li>• Não compartilhe esta tela</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Gerenciar Senha de Desenvolvedor */}
        <div className="mb-8 bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Senha de Desenvolvedor</h2>
              <p className="text-slate-600">Altere a senha de acesso às ferramentas de desenvolvedor</p>
            </div>
          </div>

          <form onSubmit={handleChangeDeveloperPassword} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nova Senha
                </label>
                <input
                  type="password"
                  value={newDeveloperPassword}
                  onChange={(e) => setNewDeveloperPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Mínimo 6 caracteres"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Confirmar Senha
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Confirme a nova senha"
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={isChangingPassword}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isChangingPassword ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Alterando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Alterar Senha
                </>
              )}
            </button>
          </form>
        </div>

        {/* Credenciais do Sistema */}
        <div className="space-y-8">
          {credentialSections.map((section, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-lg">{section.icon}</span>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">{section.title}</h2>
                  <p className="text-slate-600">Credenciais de {section.title.toLowerCase()}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.credentials.map((cred) => (
                  <CredentialField
                    key={cred.key}
                    label={cred.label}
                    value={credentials[cred.key as keyof typeof credentials]}
                    onEdit={(newValue) => handleCredentialEdit(cred.key, newValue)}
                    canEdit={cred.canEdit}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Ações Rápidas */}
        <div className="mt-8 bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Ações Rápidas</h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => {
                const allCredentials = Object.entries(credentials)
                  .map(([key, value]) => `${key}: ${value}`)
                  .join('\n');
                navigator.clipboard.writeText(allCredentials);
                alert('Todas as credenciais copiadas para a área de transferência!');
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Copy className="w-4 h-4" />
              Copiar Todas
            </button>
            <button
              onClick={() => {
                if (window.confirm('Tem certeza que deseja recarregar a página? As alterações não salvas serão perdidas.')) {
                  window.location.reload();
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Recarregar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
